import { Injectable } from '@angular/core';
import type { Layer } from "leaflet";
import { CustomerSettingsService } from "@shared/services/customerSettings/customer-settings.service";
import { BasemapService } from "@shared/services/basemap/basemap.service";
import { CustomerSettingsComponents } from "@shared/models/customer-settings";
declare const L: any;

@Injectable({providedIn: 'root'})
export class MapConfiguratorService {

  constructor(
    private customerSettingsService: CustomerSettingsService,
    private basemapService: BasemapService,
  ) {
  }

  private map!: L.Map;
  private editableLayers!: L.FeatureGroup;
  private drawOptions: any;

  /**
   * Init leaflet map for customer
   * @param customerId
   * @param drawPluginOptions
   */
  public initMap(customerId: number, drawPluginOptions: any): L.Map {
    this.map = L.map('mapProject', { editable: true });
    this.map.zoomControl.setPosition('topright');

    this.basemapService.attach(this.map, { customerId, minZoom: 3, maxZoom: 25 });

    this.setMapCenter(customerId);

    this.editableLayers = new L.FeatureGroup();
    this.map.addLayer(this.editableLayers);

    const options = drawPluginOptions ? drawPluginOptions : this.getDefaultDrawPluginOptions();
    this.drawOptions = {
      ...options,
      draw: { ...(options.draw ?? {}) },
      edit: {
        ...(options.edit ?? {}),
        featureGroup: this.editableLayers
      }
    };

    return this.map;
  }

  private setMapCenter(customerId: number): void {
    const centerCoordinates = this.customerSettingsService
      .getCenterCoordinates(customerId, CustomerSettingsComponents.RoutePlanning);

    this.map.on('locationerror', () => {
      this.map.panTo({ lat: centerCoordinates.latitude, lng: centerCoordinates.longitude });
    });

    const initialCenter = L.latLng(centerCoordinates.latitude, centerCoordinates.longitude);
    if (customerId === 1) {
      this.map.setView(initialCenter, 16);
    } else if (customerId === 3) {
      const newCenter = L.latLng(60.656023, 25.279696);
      this.map.setView(newCenter, 11);
    } else {
      this.map.setView(initialCenter, 15);
    }
  }

  private getDefaultDrawPluginOptions(): any {
    return {
      position: 'topright',
      draw: {
        polygon: false,
        polyline: false,
        circle: false,
        rectangle: false,
        marker: false,
        circlemarker: false
      },
      edit: {
        remove: false,
        edit: false
      }
    };
  }

  disableZoom(): void {
    this.map.scrollWheelZoom.disable();
    this.map.doubleClickZoom.disable();
    this.map.touchZoom.disable();
    this.map.boxZoom.disable();
    this.map.keyboard.disable();
  }

  enableZoom(): void {
    this.map.scrollWheelZoom.enable();
    this.map.doubleClickZoom.enable();
    this.map.touchZoom.enable();
    this.map.boxZoom.enable();
    this.map.keyboard.enable();
  }

  createPolylineDrawTool(): any {
    if (!this.map || !this.drawOptions) {
      throw new Error('Map must be initialized before creating a polyline draw tool.');
    }

    if (!L.Draw?.Polyline) {
      throw new Error('Leaflet.draw is not loaded. Check angular.json scripts include leaflet-draw after leaflet.');
    }

    return new L.Draw.Polyline(this.map, this.drawOptions.draw?.polyline || {});
  }

  getLayers(): Layer[] {
    return this.editableLayers.getLayers();
  }

  getEditableLayers(): L.FeatureGroup {
    return this.editableLayers;
  }
}
